/* NOOT — Set / piece selector ("Buy as").
   Points Dawn's own <variant-selects> at the chosen piece's product (variant data + URL),
   so Dawn itself re-renders price, compare-at, sale badge and the Add to cart / Sold out state
   from the real standalone product, and puts the real variant id into the product form.
   A short note under the buttons names the exact piece (e.g. "Skirt only — Mohair Ensemble Skirt").

   Gallery: choosing a piece swaps the product gallery (and the zoom lightbox) for the linked piece
   product's own gallery, loaded in the background from that product's page; choosing the set puts
   the set's own gallery back. Piece galleries are prefetched so the switch is immediate.
   The chosen piece is remembered when the shopper changes colour (linked colours), so e.g.
   Jacket stays selected and the new colour's jacket images appear.
   If a piece gallery can't be loaded, the set's gallery simply stays in place. */
(function () {
  if (customElements.get('set-piece-selector')) return;

  // Remembers the shopper's Buy as choice across a colour switch (same page view only).
  let lastChoice = 'set';

  const pageCache = new Map();
  function fetchSection(url, sectionId) {
    const key = url + '|' + sectionId;
    if (pageCache.has(key)) return pageCache.get(key);
    const sep = url.indexOf('?') === -1 ? '?' : '&';
    const p = fetch(url + sep + 'section_id=' + encodeURIComponent(sectionId), { credentials: 'same-origin' })
      .then((r) => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then((html) => new DOMParser().parseFromString(html, 'text/html'));
    p.catch(() => pageCache.delete(key));
    pageCache.set(key, p);
    return p;
  }

  customElements.define(
    'set-piece-selector',
    class SetPieceSelector extends HTMLElement {
      connectedCallback() {
        try {
          this.pieces = JSON.parse(this.querySelector('[data-set-pieces]').textContent);
        } catch (e) {
          this.pieces = {};
        }
        this.current = 'set';
        this.galleries = {}; // key -> { gallery, modal } element pairs, reused on every switch
        this.swapToken = 0;
        this.inQuickAdd = !!this.closest('quick-add-modal, .quick-add-modal');
        this.addEventListener('change', (event) => {
          const input = event.target;
          if (!input || input.name !== this.dataset.name) return;
          event.stopPropagation();
          this.select(input.value);
        });
        if (!this.inQuickAdd) this.schedulePrefetch();
      }

      // ---------- gallery ----------
      galleryId() {
        return 'MediaGallery-' + this.dataset.section;
      }
      modalId() {
        return 'ProductModal-' + this.dataset.section;
      }

      schedulePrefetch() {
        const run = () => {
          Object.keys(this.pieces).forEach((key) => this.loadGallery(key).catch(() => {}));
        };
        if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 2500 });
        else setTimeout(run, 1200);
      }

      loadGallery(key) {
        if (this.galleries[key]) return Promise.resolve(this.galleries[key]);
        const piece = this.pieces[key];
        if (!piece || !piece.url) return Promise.reject(new Error('no piece'));
        return fetchSection(piece.url, this.dataset.section).then((doc) => {
          if (this.galleries[key]) return this.galleries[key];
          const gallery = doc.getElementById(this.galleryId());
          if (!gallery || !gallery.querySelector('[data-media-id]')) throw new Error('no gallery');
          const modal = doc.getElementById(this.modalId());
          const entry = {
            gallery: document.importNode(gallery, true),
            modal: modal ? document.importNode(modal, true) : null,
          };
          // Warm the first image so it shows instantly.
          const first = entry.gallery.querySelector('img');
          if (first && first.currentSrc !== undefined) {
            const img = new Image();
            if (first.srcset) img.srcset = first.srcset;
            if (first.sizes) img.sizes = first.sizes;
            img.src = first.src;
          }
          this.galleries[key] = entry;
          return entry;
        });
      }

      rememberSetGallery() {
        if (this.galleries.set) return;
        const gallery = document.getElementById(this.galleryId());
        if (!gallery) return;
        this.galleries.set = { gallery, modal: document.getElementById(this.modalId()) };
      }

      resetGalleryPosition(gallery) {
        const slider = gallery.querySelector('.slider, [id^="Slider-Gallery"]');
        if (slider && slider.scrollLeft) slider.scrollLeft = 0;
        gallery.querySelectorAll('.is-active[data-media-id]').forEach((el) => el.classList.remove('is-active'));
        const firstItem = gallery.querySelector('[data-media-id]');
        if (firstItem) firstItem.classList.add('is-active');
        const counter = gallery.querySelector('.slider-counter--current');
        if (counter) counter.textContent = '1';
      }

      placeGallery(entry) {
        const currentGallery = document.getElementById(this.galleryId());
        if (!currentGallery || !entry || !entry.gallery) return;
        if (currentGallery !== entry.gallery) {
          entry.gallery.classList.add('sps-gallery-in');
          currentGallery.replaceWith(entry.gallery);
          this.resetGalleryPosition(entry.gallery);
          requestAnimationFrame(() =>
            requestAnimationFrame(() => entry.gallery.classList.remove('sps-gallery-in'))
          );
        }
        const currentModal = document.getElementById(this.modalId());
        if (entry.modal && currentModal && currentModal !== entry.modal) currentModal.replaceWith(entry.modal);
      }

      showGallery(key) {
        if (this.inQuickAdd) return;
        this.rememberSetGallery();
        const token = ++this.swapToken;
        if (key === 'set' || !this.pieces[key]) {
          this.placeGallery(this.galleries.set);
          return;
        }
        const gallery = document.getElementById(this.galleryId());
        if (gallery) gallery.classList.add('sps-gallery-loading');
        this.loadGallery(key)
          .then((entry) => {
            if (token === this.swapToken) this.placeGallery(entry);
          })
          .catch(() => {})
          .finally(() => {
            const g = document.getElementById(this.galleryId());
            if (g) g.classList.remove('sps-gallery-loading');
            if (this.galleries.set && this.galleries.set.gallery) {
              this.galleries.set.gallery.classList.remove('sps-gallery-loading');
            }
          });
      }

      // ---------- variants (unchanged behaviour) ----------
      getVariantSelects() {
        return document.getElementById(`variant-selects-${this.dataset.section}`);
      }

      remember(vs) {
        if (this.original) return;
        this.original = {
          variants: vs.getVariantData(),
          url: vs.dataset.url,
          updateUrl: vs.dataset.updateUrl,
        };
      }

      updateNote(key) {
        const note = this.querySelector('[data-sps-note]');
        if (!note) return;
        const piece = this.pieces[key];
        note.textContent = '';
        if (key === 'set' || !piece) {
          note.hidden = true;
          return;
        }
        const strong = document.createElement('strong');
        strong.textContent = (piece.label || '') + ' only';
        note.appendChild(strong);
        if (piece.title) note.appendChild(document.createTextNode(' — ' + piece.title));
        note.hidden = false;
      }

      select(key) {
        const vs = this.getVariantSelects();
        if (!vs || typeof vs.onVariantChange !== 'function') return;
        this.remember(vs);

        let variants;
        let url;
        if (key === 'set' || !this.pieces[key]) {
          key = 'set';
          variants = this.original.variants;
          url = this.original.url;
          if (this.original.updateUrl === undefined) delete vs.dataset.updateUrl;
          else vs.dataset.updateUrl = this.original.updateUrl;
        } else {
          // Piece gallery is swapped in separately, so never ask Dawn to switch media.
          variants = this.pieces[key].variants.map((v) => Object.assign({}, v, { featured_media: null }));
          url = this.pieces[key].url;
          // Keep the address bar on the Set page.
          vs.dataset.updateUrl = 'false';
        }

        this.current = key;
        lastChoice = key;
        this.updateNote(key);
        // Gallery first (the set gallery must be back before Dawn looks up the set's featured image).
        this.showGallery(key);
        vs.variantData = variants;
        vs.dataset.url = url;
        this.refreshAvailability(vs, variants);
        vs.onVariantChange({ target: vs });
      }

      // Re-apply a choice after a colour switch, if this colour has that piece.
      restore(key) {
        if (!key || key === 'set' || !this.pieces[key]) {
          lastChoice = 'set';
          return;
        }
        const input = this.querySelector(`input[name="${this.dataset.name}"][value="${key}"]`);
        if (!input) return;
        input.checked = true;
        this.select(key);
      }

      // Dawn only recomputes availability for the 2nd+ option, so mark every option value here.
      refreshAvailability(vs, variants) {
        const wrappers = Array.from(vs.querySelectorAll('.product-form__input'));
        const selected = wrappers.map((w) => {
          const checked = w.querySelector('input:checked');
          if (checked) return checked.value;
          const select = w.querySelector('select');
          return select ? select.value : null;
        });

        wrappers.forEach((wrapper, index) => {
          wrapper.querySelectorAll('input[type="radio"], option').forEach((el) => {
            const value = el.getAttribute('value');
            const available = variants.some((variant) => {
              if (!variant.available) return false;
              for (let i = 0; i < index; i++) {
                if (variant.options[i] !== selected[i]) return false;
              }
              return variant.options[index] === value;
            });
            if (el.tagName === 'INPUT') {
              el.classList.toggle('disabled', !available);
            } else if (window.variantStrings) {
              el.innerText = available
                ? value
                : window.variantStrings.unavailable_with_option.replace('[value]', value);
            }
          });
        });
      }
    }
  );

  // Colour switch (linked colours) replaces the product section: keep the shopper's Buy as choice.
  document.addEventListener('linked-colors:changed', () => {
    const choice = lastChoice;
    if (choice === 'set') return;
    const selector = document.querySelector('#MainContent set-piece-selector');
    if (selector && typeof selector.restore === 'function') selector.restore(choice);
    else lastChoice = 'set';
  });

  // Smooth, short fade when a gallery is swapped.
  const style = document.createElement('style');
  style.textContent =
    'media-gallery{transition:opacity .18s ease}' +
    'media-gallery.sps-gallery-in,media-gallery.sps-gallery-loading{opacity:.35}';
  document.head.appendChild(style);
})();
